/**
 * API Service for Super Admin Reporting Endpoints
 * Proxies through TerraBE to POS Backend reporting API
 */

import { api } from 'src/api/config';

const API_BASE_URL = api.hostname;

/**
 * Fetch platform-wide metrics
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} token - Auth token
 * @returns {Promise} Platform metrics data
 */
export async function fetchPlatformMetrics(params = {}, token) {
  const queryParams = new URLSearchParams();
  
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  
  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/reporting/platform-metrics/${queryString ? '?' + queryString : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Fetch Financial Institution metrics
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} token - Auth token
 * @returns {Promise} Array of FI metrics
 */
export async function fetchFIMetrics(params = {}, token) {
  const queryParams = new URLSearchParams();
  
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  
  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/reporting/fi-metrics/${queryString ? '?' + queryString : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Fetch Account metrics
 * @param {Object} params - Query parameters
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {number} params.fi_id - Filter by Financial Institution ID
 * @param {string} token - Auth token
 * @returns {Promise} Array of account metrics
 */
export async function fetchAccountMetrics(params = {}, token) {
  const queryParams = new URLSearchParams();
  
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);
  if (params.fi_id) queryParams.append('fi_id', params.fi_id);
  
  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/reporting/account-metrics/${queryString ? '?' + queryString : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Fetch FI Merchant metrics (for FI Admin Overview)
 * Returns per-merchant breakdown for a specific FI
 * @param {Object} params - Query parameters
 * @param {string} params.terra_fi_id - Financial Institution ID (required)
 * @param {string} params.start_date - Start date (YYYY-MM-DD)
 * @param {string} params.end_date - End date (YYYY-MM-DD)
 * @param {string} token - Auth token
 * @returns {Promise} FI summary + merchants array with metrics
 */
export async function fetchFIMerchantMetrics(params = {}, token) {
  const queryParams = new URLSearchParams();

  if (params.terra_fi_id) queryParams.append('terra_fi_id', params.terra_fi_id);
  if (params.start_date) queryParams.append('start_date', params.start_date);
  if (params.end_date) queryParams.append('end_date', params.end_date);

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/reporting/fi-merchant-metrics/${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch all Financial Institutions
 * @param {string} token - Auth token
 * @returns {Promise} Array of Financial Institutions
 */
export async function fetchFinancialInstitutions(token) {
  const url = `${API_BASE_URL}/api/v1/fis/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch merchant terminals
 * @param {Object} params - Query parameters
 * @param {number} params.fi_id - Financial Institution ID
 * @param {number} params.account_id - Account (Merchant) ID
 * @param {string} token - Auth token
 * @returns {Promise} Array of terminals
 */
export async function fetchMerchantTerminals(params = {}, token) {
  const { fi_id, account_id } = params;

  if (!fi_id || !account_id) {
    throw new Error('fi_id and account_id are required');
  }

  const url = `${API_BASE_URL}/api/v1/transactions/merchant-terminals/${fi_id}/${account_id}/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch merchant transaction summary
 * @param {Object} params - Query parameters
 * @param {number} params.fi_id - Financial Institution ID
 * @param {number} params.account_id - Account (Merchant) ID
 * @param {string} params.date_from - Start date (YYYY-MM-DD)
 * @param {string} params.date_to - End date (YYYY-MM-DD)
 * @param {string} token - Auth token
 * @returns {Promise} Merchant transaction summary
 */
export async function fetchMerchantSummary(params = {}, token) {
  const { fi_id, account_id, date_from, date_to } = params;

  if (!fi_id || !account_id) {
    throw new Error('fi_id and account_id are required');
  }

  const queryParams = new URLSearchParams();
  if (date_from) queryParams.append('date_from', date_from);
  if (date_to) queryParams.append('date_to', date_to);

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/transactions/summary/${fi_id}/${account_id}/${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch payment issues summary (underfunded, overfunded, unexpected)
 * @param {Object} params - Query parameters
 * @param {string} params.terra_fi_id - Filter by Financial Institution ID
 * @param {string} params.terra_account_id - Filter by Merchant Account ID
 * @param {string} token - Auth token
 * @returns {Promise} Payment issues summary
 */
export async function fetchPaymentIssues(params = {}, token) {
  const queryParams = new URLSearchParams();

  if (params.terra_fi_id) queryParams.append('terra_fi_id', params.terra_fi_id);
  if (params.terra_account_id) queryParams.append('terra_account_id', params.terra_account_id);

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v1/reporting/payment-issues/${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/**
 * Resolve a payment session discrepancy (underfunded/overfunded)
 * @param {string} sessionId - The payment session ID to resolve
 * @param {Object} payload - Resolution payload
 * @param {string} payload.action - Resolution action: 'accept_partial' | 'deny' | 'keep_full' | 'partial_refund'
 * @param {number} payload.accepted_amount_usd - For accept_partial/partial_refund: amount to accept
 * @param {number} payload.refund_amount_usd - For partial_refund: amount being refunded
 * @param {string} payload.refund_method - For partial_refund: 'store_credit' | 'check' | 'paypal' | 'ach' | 'other'
 * @param {string} payload.refund_notes - Optional refund notes
 * @param {string} payload.reference_number - Optional refund reference number
 * @param {string} payload.notes - Optional resolution notes
 * @param {string} token - Auth token
 * @returns {Promise} Resolution result
 */
export async function resolvePaymentSession(sessionId, payload, token) {
  const url = `${API_BASE_URL}/api/v1/transactions/resolve/${sessionId}/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Preview fees for resolution decisions (batch)
 *
 * Calculates what the fees and net payout would be for one or more actions,
 * respecting the session's fee_payer mode and fee configuration.
 * This is the single source of truth - matches backend calculation exactly.
 *
 * @param {string} sessionId - Payment session ID
 * @param {Array} previews - Preview requests
 * @param {number} previews[].amount_usd - Amount to calculate fees on
 * @param {string} previews[].action - 'accept_partial' | 'keep_full' | 'partial_refund' | 'deny'
 * @param {string} token - Auth token
 * @returns {Promise} Fee preview batch response
 */
export async function getFeePreviews(sessionId, previews, token) {
  const url = `${API_BASE_URL}/api/v1/transactions/fee-preview/${sessionId}/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ previews }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Mark a payment session as manually refunded
 *
 * This is for MVP where merchants handle refunds manually
 * (via PayPal, check, store credit, etc.) and record it in the system.
 *
 * @param {string} sessionId - Payment session ID
 * @param {object} payload - Refund details
 * @param {number} payload.refund_amount_usd - Amount refunded to customer
 * @param {string} payload.refund_method - 'store_credit' | 'check' | 'paypal' | 'ach' | 'other'
 * @param {string} payload.notes - Optional notes about the refund
 * @param {string} payload.reference_number - Optional refund reference number
 * @param {string} token - Auth token
 * @returns {Promise} Refund result with updated session data
 */
export async function markPaymentRefunded(sessionId, payload, token) {
  const url = `${API_BASE_URL}/api/v1/transactions/mark-refunded/${sessionId}/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Get current refund state for a payment session
 *
 * This enables the "query-first" pattern where clients check existing refunds
 * and remaining refundable amount before attempting to create a new refund.
 *
 * NOTE: TerraBE now proxies this to the POS /status/ endpoint which includes
 * all refund state fields (remaining_refundable_usd, can_refund, refunds).
 *
 * @param {string} sessionId - Payment session ID
 * @param {string} token - Auth token
 * @returns {Promise} Refund state data (plus additional status fields)
 */
export async function getRefundState(sessionId, token) {
  const url = `${API_BASE_URL}/api/v1/transactions/refund-state/${sessionId}/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/**
 * Fetch dashboard alerts for SuperAdmin
 *
 * Returns alerts for:
 * - Unsettled payments (paid but no Settlement record)
 * - Failed settlement webhooks (pending retry or permanently failed)
 *
 * @param {string} token - Auth token
 * @returns {Promise} Dashboard alerts data
 */
export async function fetchDashboardAlerts(token) {
  const url = `${API_BASE_URL}/api/v1/dashboard/alerts/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    // Return empty alerts on error rather than throwing
    console.warn('Failed to fetch dashboard alerts:', response.status);
    return { alerts: [], summary: {} };
  }

  return response.json();
}
